// client/src/components/pages/PeriodSummary/TimelineView.tsx (converted from .js, ST-00209)
//
// Period Summary — Timeline view (ST-00174).
//
// One swimlane per Ref No / Order No across the calendar of the period, so the
// eye reads WHEN each key moved and how far receipt lags dispatch.
//
// Why the lanes are hand-rolled divs rather than 50–200 recharts charts: a
// chart per lane would mount hundreds of SVG trees and ResponsiveContainer
// observers. Only the single overall daily chart at the top uses recharts.
//
// UX pass:
//   * lanes are 64px with 26px of bar per side (was 44/20), so a 10% difference
//     between two days is actually visible
//   * the hover tooltip is a portal driven through a ref — lanes never re-render
//     on mouse move, and the tooltip cannot be clipped by the scroll container
//   * the overall chart collapses (remembered in prefs) to give the lanes room
//
// ST-00209: lanes are divided by ROUTE_BOUNDARY_CLASS, one step stronger than a lane's own
// zero baseline, so a lane's receipt bars (down) never read as the next lane's dispatch (up).

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FaExclamationCircle, FaInbox, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useThemeTokens } from '../../../utils/themeTokens';
import { getPeriodSummaryTimeline } from '../../../services/periodSummaryService';
import GroupDetailJs from './GroupDetail';
import { DayTooltipBody, RechartsDayTooltip as RechartsDayTooltipJs } from './DailyChartTooltip';
import { ROUTE_BOUNDARY_CLASS } from './routeBoundary';
import {
  DISPATCH_COLOR,
  RECEIPT_COLOR,
  MONTHS,
  dayParts,
  isWeekend,
  formatDayShort,
  formatAxisNumber,
  formatCount,
  formatTonnesCompact,
  groupByLabel,
  readPrefs,
  writePrefs,
} from './utils';
import type {
  PeriodBreakdownCache,
  PeriodFilterRoute,
  PeriodGroupDetailProps,
  PeriodLoadBreakdown,
  PeriodQueryParams,
  PeriodSide,
  PeriodTimelineBody,
  PeriodTimelineDay,
  PeriodTimelineGroup,
} from '../../../types/clientPages/periodSummary';

// Typed boundaries for the still-JS siblings (their inferred prop types are too loose to use).
const GroupDetail = GroupDetailJs as unknown as ComponentType<PeriodGroupDetailProps>;
/** recharts clones the `content` element with active / payload / label. */
const RechartsDayTooltip = RechartsDayTooltipJs as unknown as ComponentType<object>;

const LABEL_W = 240; // px — the sticky left column
const DAY_W = 16; // px — narrowest a calendar day column may get (spec floor)
const MAX_DAY_W = 40; // px — widest, so a 7-day period doesn't become a bar chart
const MAX_BAR_W = 14; // px — bars stay bars even when the column is wide
const LANE_H = 64; // px — lane height; the butterfly baseline sits at LANE_H/2
const BAR_SPAN = 26; // px — tallest bar on either side of the baseline
const DEFAULT_LIMIT = 50;
const EXPANDED_LIMIT = 200;
const TOOLTIP_W = 230;

type LaneScale = 'shared' | 'lane';

interface ToggleOption<V extends string> {
  value: V;
  label: string;
}

const SIDE_OPTIONS: ToggleOption<PeriodSide>[] = [
  { value: 'both', label: 'Both' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'receipt', label: 'Receipt' },
];

const SCALE_OPTIONS: ToggleOption<LaneScale>[] = [
  { value: 'shared', label: 'Shared' },
  { value: 'lane', label: 'Per lane' },
];

interface PillToggleProps<V extends string> {
  label: string;
  options: ToggleOption<V>[];
  value: V;
  onChange: (value: V) => void;
}

const PillToggle = <V extends string>({ label, options, value, onChange }: PillToggleProps<V>) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const idx = options.findIndex((o) => o.value === value);
    const next = options[(idx + delta + options.length) % options.length];
    if (next) onChange(next.value);
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
      <div
        className="flex items-center bg-gray-100 rounded-md p-0.5"
        role="radiogroup"
        aria-label={label}
        onKeyDown={handleKeyDown}
      >
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(opt.value)}
              className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 ${
                selected
                  ? 'bg-surface text-slate-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** The mirrored-bar legend, drawn as the marks it explains rather than described. */
const MirrorLegend = () => (
  <span className="inline-flex items-center gap-3 text-[11px] text-gray-500">
    <span className="inline-flex items-center gap-1.5">
      <svg width="12" height="16" aria-hidden="true">
        <rect x="2" y="1" width="8" height="7" fill={DISPATCH_COLOR} rx="1" />
        <rect x="0" y="8" width="12" height="1" className="text-gray-300" fill="currentColor" />
      </svg>
      dispatch up
    </span>
    <span className="inline-flex items-center gap-1.5">
      <svg width="12" height="16" aria-hidden="true">
        <rect x="0" y="7" width="12" height="1" className="text-gray-300" fill="currentColor" />
        <rect x="2" y="8" width="8" height="7" fill={RECEIPT_COLOR} rx="1" />
      </svg>
      receipt down
    </span>
  </span>
);

const TimelineSkeleton = () => (
  <div className="animate-pulse space-y-3">
    <div className="bg-surface rounded-lg border border-gray-200 h-48" />
    <div className="bg-surface rounded-lg border border-gray-200 overflow-hidden">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
          <div className="h-3 bg-gray-200 rounded w-40" />
          <div className="h-6 bg-gray-100 rounded flex-1" />
        </div>
      ))}
    </div>
  </div>
);

// ── Cursor tooltip ────────────────────────────────────────────────────────────

/**
 * A cursor-following tooltip that lives in a portal on <body>.
 *
 * Two reasons it is imperative (a ref with show/hide) rather than state on the
 * view: the swimlane grid is inside `overflow-x-auto`, so an in-tree tooltip
 * gets clipped at the right edge; and hover must not re-render 200 lanes, which
 * a `useState` in the parent would do on every mousemove.
 */
/** What the view holds on to: show a body at a cursor position, or hide it. */
interface CursorTooltipHandle {
  show: (content: ReactNode, x: number, y: number) => void;
  hide: () => void;
}

interface CursorTooltipState {
  content: ReactNode;
  x: number;
  y: number;
}

const CursorTooltip = forwardRef<CursorTooltipHandle, object>((_props, ref) => {
  const [state, setState] = useState<CursorTooltipState | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      show: (content, x, y) => setState({ content, x, y }),
      hide: () => setState(null),
    }),
    []
  );

  if (!state) return null;

  const flipX = state.x + TOOLTIP_W + 24 > window.innerWidth;
  const style: CSSProperties = {
    position: 'fixed',
    left: flipX ? Math.max(8, state.x - TOOLTIP_W - 14) : state.x + 14,
    top: Math.min(state.y + 16, Math.max(8, window.innerHeight - 150)),
    width: TOOLTIP_W,
    zIndex: 60,
  };

  return createPortal(
    <div
      role="tooltip"
      style={style}
      className="pointer-events-none rounded-md border border-gray-200 bg-surface-raised shadow-lg px-2.5 py-2 text-[11px] text-slate-800"
    >
      {state.content}
    </div>,
    document.body
  );
});
CursorTooltip.displayName = 'CursorTooltip';

// ── Overall daily chart ───────────────────────────────────────────────────────

interface OverallDailyChartProps {
  days: string[];
  groups: PeriodTimelineGroup[];
  open: boolean;
  onToggle: () => void;
}

/** One calendar day of the overall chart: every lane's tonnes and tickets added up. */
interface DayBucket {
  date: string;
  dispatchTonnes: number;
  receiptTonnes: number;
  tickets: number;
}

const OverallDailyChart = ({ days, groups, open, onToggle }: OverallDailyChartProps) => {
  const t = useThemeTokens();

  const data = useMemo(() => {
    const byDate = new Map<string, DayBucket>(
      days.map((d) => [d, { date: d, dispatchTonnes: 0, receiptTonnes: 0, tickets: 0 }])
    );
    groups.forEach((g) => {
      (g.days || []).forEach((d) => {
        const bucket = byDate.get(d.date);
        if (!bucket) return;
        bucket.dispatchTonnes += Number(d.dispatchTonnes) || 0;
        bucket.receiptTonnes += Number(d.receiptTonnes) || 0;
        bucket.tickets += Number(d.tickets) || 0;
      });
    });
    return days.map((d) => ({ ...byDate.get(d), label: formatDayShort(d) }));
  }, [days, groups]);

  return (
    <div className="bg-surface rounded-lg border border-gray-200 shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50"
      >
        {open ? (
          <FaChevronDown className="text-[10px] text-gray-400" />
        ) : (
          <FaChevronRight className="text-[10px] text-gray-400" />
        )}
        <h2 className="text-xs font-semibold text-slate-800">Daily tonnes — all lanes</h2>
        <span className="ml-auto text-[10px] text-gray-400">
          {open ? 'Hide chart' : 'Show chart'}
        </span>
      </button>
      {open && (
        <div className="h-48 w-full px-2 pb-3" data-testid="timeline-overall-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
              <XAxis
                dataKey="label"
                stroke={t.axis}
                tick={{ fill: t.axisLabel, fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke={t.axis}
                tick={{ fill: t.axisLabel, fontSize: 10 }}
                tickFormatter={formatAxisNumber}
                width={44}
              />
              <Tooltip content={<RechartsDayTooltip />} cursor={{ fill: t.cursor }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="dispatchTonnes"
                name="Dispatch t"
                fill={DISPATCH_COLOR}
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="receiptTonnes"
                name="Receipt t"
                fill={RECEIPT_COLOR}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ── Lane ──────────────────────────────────────────────────────────────────────

interface LaneProps {
  group: PeriodTimelineGroup;
  days: string[];
  dayW: number;
  side: PeriodSide;
  laneMax: number | undefined;
  sharedMax: number;
  scale: LaneScale;
  expanded: boolean;
  onToggle: (key: string) => void;
  keyLabel: string;
  onHover: (content: ReactNode, x: number, y: number) => void;
  onLeave: () => void;
}

const Lane = ({
  group,
  days,
  dayW,
  side,
  laneMax,
  sharedMax,
  scale,
  expanded,
  onToggle,
  keyLabel,
  onHover,
  onLeave,
}: LaneProps) => {
  const byDate = useMemo(() => {
    const map = new Map<string, PeriodTimelineDay>();
    (group.days || []).forEach((d) => map.set(d.date, d));
    return map;
  }, [group.days]);

  const max = (scale === 'lane' ? laneMax : sharedMax) || 1;
  const half = LANE_H / 2;
  const mirrored = side === 'both';
  const span = mirrored ? BAR_SPAN : LANE_H - 8;
  const baseline = mirrored ? half : LANE_H - 4;

  const barHeight = (tonnes: number | null | undefined) => {
    const v = Number(tonnes) || 0;
    if (v <= 0) return 0;
    return Math.max(1, Math.min(span, (v / max) * span));
  };

  const barW = Math.max(3, Math.min(dayW - 4, MAX_BAR_W));
  const barX = (dayW - barW) / 2;

  return (
    <div
      data-testid={`timeline-lane-row-${group.key}`}
      className={`flex ${ROUTE_BOUNDARY_CLASS} ${expanded ? 'bg-primary-500/5' : 'hover:bg-gray-50'}`}
    >
      {/* Sticky label column — three tidy lines, never a truncated run-on. */}
      <div
        className="sticky left-0 z-10 flex-shrink-0 px-2 py-1.5 border-r border-gray-200 bg-surface"
        style={{ width: LABEL_W }}
      >
        <button
          type="button"
          onClick={() => onToggle(group.key)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.key}`}
          className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 rounded"
        >
          <div className="flex items-center gap-1 min-w-0">
            {expanded ? (
              <FaChevronDown className="text-[8px] text-gray-400 flex-shrink-0" />
            ) : (
              <FaChevronRight className="text-[8px] text-gray-400 flex-shrink-0" />
            )}
            <span
              className="text-[11px] font-semibold text-slate-800 truncate"
              title={`${keyLabel}: ${group.key}`}
            >
              {group.key}
            </span>
          </div>
          <div className="text-[10px] text-gray-500 font-mono tabular-nums truncate pl-3">
            {formatTonnesCompact(group.nettTonnes)} t · {formatCount(group.tickets)} tkt
          </div>
          <div className="flex items-center gap-1.5 pl-3 min-w-0">
            <span className="text-[10px] text-gray-400 truncate">
              {formatDayShort(group.firstDay)} → {formatDayShort(group.lastDay)}
            </span>
            <span className="text-[10px] font-mono tabular-nums whitespace-nowrap ml-auto">
              <span style={{ color: DISPATCH_COLOR }}>
                {formatTonnesCompact(group.dispatchTonnes)}
              </span>
              <span className="text-gray-300"> / </span>
              <span style={{ color: RECEIPT_COLOR }}>
                {formatTonnesCompact(group.receiptTonnes)}
              </span>
            </span>
          </div>
        </button>
      </div>

      {/* Day columns */}
      <div
        className="relative flex"
        style={{ height: LANE_H }}
        data-testid={`timeline-lane-${group.key}`}
      >
        {/* Baseline + the 50%-of-scale references, drawn once per lane. */}
        <div
          className="absolute left-0 right-0 bg-gray-200 pointer-events-none"
          style={{ top: baseline - 0.5, height: 1 }}
        />
        <div
          className="absolute left-0 right-0 border-t border-dashed border-gray-200 opacity-50 pointer-events-none"
          style={{ top: baseline - span / 2 }}
        />
        {mirrored && (
          <div
            className="absolute left-0 right-0 border-t border-dashed border-gray-200 opacity-50 pointer-events-none"
            style={{ top: baseline + span / 2 }}
          />
        )}

        {days.map((date) => {
          const d = byDate.get(date);
          const weekend = isWeekend(date);
          const monthStart = dayParts(date).day === 1;

          const handleMove = (event: MouseEvent<HTMLDivElement>) => {
            if (!d) return onLeave();
            return onHover(
              <DayTooltipBody
                title={group.key}
                subtitle={formatDayShort(date)}
                dispatchTonnes={d.dispatchTonnes}
                receiptTonnes={d.receiptTonnes}
                suffix={{
                  dispatch: ` (${formatCount(d.dispatchTickets)})`,
                  receipt: ` (${formatCount(d.receiptTickets)})`,
                }}
              />,
              event.clientX,
              event.clientY
            );
          };

          return (
            <div
              key={date}
              onMouseMove={handleMove}
              onMouseLeave={onLeave}
              className={`relative flex-shrink-0 ${weekend ? 'bg-gray-100/40' : ''} ${
                monthStart ? 'border-l border-gray-300' : ''
              }`}
              style={{ width: dayW, height: LANE_H }}
            >
              {!d && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 bg-gray-200"
                  style={{ top: baseline - 0.5, width: Math.max(4, dayW - 8), height: 1 }}
                />
              )}
              {d && mirrored && (
                <>
                  <div
                    data-testid="timeline-bar-up"
                    className="absolute rounded-t-sm"
                    style={{
                      left: barX,
                      width: barW,
                      bottom: half,
                      height: barHeight(d.dispatchTonnes),
                      backgroundColor: DISPATCH_COLOR,
                    }}
                  />
                  <div
                    data-testid="timeline-bar-down"
                    className="absolute rounded-b-sm"
                    style={{
                      left: barX,
                      width: barW,
                      top: half,
                      height: barHeight(d.receiptTonnes),
                      backgroundColor: RECEIPT_COLOR,
                    }}
                  />
                </>
              )}
              {d && !mirrored && (
                <div
                  data-testid="timeline-bar-single"
                  className="absolute rounded-t-sm"
                  style={{
                    left: barX,
                    width: barW,
                    bottom: 4,
                    height: barHeight(side === 'dispatch' ? d.dispatchTonnes : d.receiptTonnes),
                    backgroundColor: side === 'dispatch' ? DISPATCH_COLOR : RECEIPT_COLOR,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Hovering a day must not re-render every other lane (the view holds no hover
// state at all, but sorting / scale changes still have to get through).
const MemoLane = React.memo(Lane);

// ── Day axis ──────────────────────────────────────────────────────────────────

interface DayAxisProps {
  days: string[];
  dayW: number;
  keyLabel: string;
  scaleCaption: string;
}

const DayAxis = ({ days, dayW, keyLabel, scaleCaption }: DayAxisProps) => {
  const monthMarks = useMemo(() => {
    const marks: Array<{ date: string; index: number; label: string }> = [];
    days.forEach((date, index) => {
      const { day, month, year } = dayParts(date);
      if (index === 0 || day === 1) {
        marks.push({
          date,
          index,
          label:
            month === 1 || index === 0 ? `${MONTHS[month - 1]} ${year}` : (MONTHS[month - 1] ?? ''),
        });
      }
    });
    return marks;
  }, [days]);

  return (
    <div className="flex sticky top-0 z-20 bg-surface border-b border-gray-200">
      <div
        className="sticky left-0 z-30 bg-surface flex-shrink-0 border-r border-gray-200 px-2 py-1"
        style={{ width: LABEL_W }}
      >
        <div className="text-[10px] uppercase tracking-wide text-gray-400">{keyLabel}</div>
        <div className="text-[10px] text-gray-400 truncate" title={scaleCaption}>
          {scaleCaption}
        </div>
      </div>
      <div style={{ width: days.length * dayW }}>
        <div className="relative h-4">
          {monthMarks.map((m) => (
            <span
              key={m.date}
              className="absolute top-0 text-[10px] font-semibold text-slate-800 whitespace-nowrap px-1"
              style={{ left: m.index * dayW }}
            >
              {m.label}
            </span>
          ))}
        </div>
        <div className="flex">
          {days.map((date) => {
            const weekend = isWeekend(date);
            const { day } = dayParts(date);
            return (
              <div
                key={date}
                title={date}
                className={`flex-shrink-0 text-center text-[10px] leading-4 pb-1 ${
                  weekend ? 'bg-gray-100/40 text-gray-300' : 'text-gray-500'
                } ${day === 1 ? 'border-l border-gray-300' : ''}`}
                style={{ width: dayW }}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── View ──────────────────────────────────────────────────────────────────────

export interface TimelineViewProps {
  /** { startDate, endDate, groupBy, ticketType, route filter }, memoised by the page. */
  query: PeriodQueryParams;
  /** Debounced; sent to the API because the endpoint returns only the top `limit` lanes. */
  searchTerm?: string;
  /** Page-level breakdown cache. */
  breakdowns: PeriodBreakdownCache;
  onLoadBreakdown: PeriodLoadBreakdown;
  onFilterRoute?: PeriodFilterRoute;
  /** Bumped by the page's Refresh button. */
  refreshToken?: number;
}

const TimelineView = ({
  query,
  searchTerm,
  breakdowns,
  onLoadBreakdown,
  onFilterRoute,
  refreshToken,
}: TimelineViewProps) => {
  const [side, setSide] = useState<PeriodSide>(() =>
    query.ticketType === 'Dispatch'
      ? 'dispatch'
      : query.ticketType === 'Receipt'
        ? 'receipt'
        : 'both'
  );
  const [scale, setScale] = useState<LaneScale>('shared');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [chartOpen, setChartOpen] = useState(() => readPrefs().timelineChartOpen !== false);

  const [data, setData] = useState<PeriodTimelineBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // The day columns stretch to fill the width rather than leaving 300px of dead
  // space to the right of a short period — wider columns are strictly easier to
  // read, so the floor (DAY_W) only ever applies when the period is long.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dayW, setDayW] = useState(DAY_W);

  const tooltipRef = useRef<CursorTooltipHandle>(null);
  const showTooltip = useCallback(
    (content: ReactNode, x: number, y: number) => tooltipRef.current?.show(content, x, y),
    []
  );
  const hideTooltip = useCallback(() => tooltipRef.current?.hide(), []);

  // A single ticket type makes the opposite side permanently empty, so the
  // mirrored view would be half blank — follow the page-level filter.
  useEffect(() => {
    if (query.ticketType === 'Dispatch') setSide('dispatch');
    else if (query.ticketType === 'Receipt') setSide('receipt');
    else setSide('both');
  }, [query.ticketType]);

  // A new period / grouping / search resets back to the first page of lanes.
  useEffect(() => {
    setLimit(DEFAULT_LIMIT);
  }, [query, searchTerm]);

  const toggleChart = useCallback(() => {
    setChartOpen((prev) => {
      writePrefs({ ...readPrefs(), timelineChartOpen: !prev });
      return !prev;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await getPeriodSummaryTimeline({
        ...query,
        search: searchTerm || undefined,
        limit,
      });
      setData(body);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string } | null;
      setError(e?.response?.data?.message || e?.message || 'Failed to load the timeline');
    } finally {
      setLoading(false);
    }
  }, [query, searchTerm, limit]);

  useEffect(() => {
    setExpandedKey(null);
    load();
  }, [load, refreshToken]);

  // Never leave a tooltip stranded when the lanes underneath it go away.
  useEffect(() => hideTooltip, [hideTooltip, data]);

  const days = useMemo(() => data?.days || [], [data]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !days.length) return undefined;
    const compute = () => {
      const avail = el.clientWidth - LABEL_W;
      if (avail <= 0) return;
      setDayW(Math.max(DAY_W, Math.min(MAX_DAY_W, Math.floor(avail / days.length))));
    };
    compute();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [days.length]);
  const groups = useMemo(() => data?.groups || [], [data]);

  const laneMaxima = useMemo(() => {
    const map = new Map<string, number>();
    groups.forEach((g) => {
      let max = 0;
      (g.days || []).forEach((d) => {
        max = Math.max(max, Number(d.dispatchTonnes) || 0, Number(d.receiptTonnes) || 0);
      });
      map.set(g.key, max);
    });
    return map;
  }, [groups]);

  const sharedMax = useMemo(() => {
    let max = 0;
    laneMaxima.forEach((v) => {
      max = Math.max(max, v);
    });
    return max;
  }, [laneMaxima]);

  // Lanes always run in first-day order: the timeline reads top-to-bottom as
  // "what started when", which is the whole point of the view. Groups without
  // a first day sink to the bottom; ties fall back to heaviest first, then key.
  const orderedGroups = useMemo(() => {
    const rows = [...groups];
    rows.sort((a, b) => {
      const da = a.firstDay ? String(a.firstDay) : '\uffff';
      const db = b.firstDay ? String(b.firstDay) : '\uffff';
      if (da !== db) return da < db ? -1 : 1;
      const t = (Number(b.nettTonnes) || 0) - (Number(a.nettTonnes) || 0);
      if (t !== 0) return t;
      return String(a.key).localeCompare(String(b.key), undefined, { numeric: true });
    });
    return rows;
  }, [groups]);

  const handleToggleLane = useCallback(
    (key: string) => setExpandedKey((prev) => (prev === key ? null : key)),
    []
  );

  const keyLabel = groupByLabel(query.groupBy);
  const gridWidth = LABEL_W + days.length * dayW;
  const scaleCaption =
    scale === 'lane'
      ? 'bar = that lane’s own peak day'
      : `bar = up to ${formatTonnesCompact(sharedMax)} t/day`;

  if (loading && !data) return <TimelineSkeleton />;

  if (error) {
    return (
      <div
        role="alert"
        className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center justify-between gap-3"
      >
        <span className="flex items-center gap-2">
          <FaExclamationCircle className="flex-shrink-0" />
          {error}
        </span>
        <button
          type="button"
          onClick={load}
          className="px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100 font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <CursorTooltip ref={tooltipRef} />

      <OverallDailyChart days={days} groups={groups} open={chartOpen} onToggle={toggleChart} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PillToggle label="Side" options={SIDE_OPTIONS} value={side} onChange={setSide} />
        <PillToggle label="Scale" options={SCALE_OPTIONS} value={scale} onChange={setScale} />
        <MirrorLegend />
      </div>

      {orderedGroups.length === 0 ? (
        <div className="bg-surface rounded-lg border border-gray-200 p-10 text-center">
          <FaInbox className="text-3xl text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {searchTerm
              ? `No ${keyLabel} matches “${searchTerm}”`
              : `No tickets with a ${keyLabel} in this period`}
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto" ref={scrollRef}>
            <div style={{ minWidth: gridWidth }}>
              <DayAxis days={days} dayW={dayW} keyLabel={keyLabel} scaleCaption={scaleCaption} />

              {orderedGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <MemoLane
                    group={g}
                    days={days}
                    dayW={dayW}
                    side={side}
                    laneMax={laneMaxima.get(g.key)}
                    sharedMax={sharedMax}
                    scale={scale}
                    expanded={expandedKey === g.key}
                    onToggle={handleToggleLane}
                    keyLabel={keyLabel}
                    onHover={showTooltip}
                    onLeave={hideTooltip}
                  />
                  {expandedKey === g.key && (
                    <div className="sticky left-0" style={{ width: '100%', maxWidth: '100vw' }}>
                      <GroupDetail
                        groupKey={g.key}
                        groupBy={query.groupBy}
                        query={query}
                        breakdowns={breakdowns}
                        onLoadBreakdown={onLoadBreakdown}
                        onFilterRoute={onFilterRoute}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {data?.truncated && (
            <div className="px-4 py-2.5 border-t border-gray-100 text-[11px] text-gray-500 flex items-center justify-between gap-3">
              <span>
                Showing the top {orderedGroups.length.toLocaleString()} lanes by nett tonnes.
              </span>
              {limit < EXPANDED_LIMIT && (
                <button
                  type="button"
                  onClick={() => setLimit(EXPANDED_LIMIT)}
                  className="px-2.5 py-1 rounded border border-gray-200 bg-surface text-slate-800 font-medium hover:bg-gray-50"
                >
                  Show more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineView;
